import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, CheckCircle2, Circle, Download, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BRAND } from "@/lib/constants";
import { formatBytes, formatDate } from "./DocumentStats";
import EditableCell from "@/components/shared/EditableCell";
import SendEmailDialog from "./SendEmailDialog";
import { doneNoteText } from "./MarkDoneDialog";
import { DOC_TYPE_OPTIONS } from "@/constants/canonical";
import { getPreviewUrl, prefetchPreviewUrls, prefetchThumbUrls } from "@/lib/previewUrls";
import { viewerPath } from "@/lib/viewer";
import { isPdfDoc } from "@/lib/isPdf";
import { useDocSizes } from "./useDocSizes";

// DocThumb pulls in pdfjs: keep it out of the main chunk.
const DocThumb = lazy(() => import("./DocThumb"));

// Fresh SAS every time (the list's own download_url goes stale after 15 min).
async function downloadDoc(blobName) {
  const p = await getPreviewUrl(blobName);
  if (!p?.download_url) {
    toast.error("Download unavailable");
    return;
  }
  const a = document.createElement("a");
  a.href = p.download_url;
  a.download = p.filename || "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const CURRENCY_OPTIONS = ["AUD", "CAD", "CHF", "EUR", "GBP", "ILS", "JPY", "USD"];

// ponytail: pin the Document column so the inline Done/New badges shift only
// the filename (within the cell), never reflow the rest of the row. 20rem holds
// both badges + the max-w-[200px] truncated filename, so the column never grows.
const DOC_COL_WIDTH = "20rem";

const columns = [
  { key: "original_filename", label: "Document", sortable: true, sortField: "document_name", width: DOC_COL_WIDTH },
  { key: "portal", label: "Portal", sortable: true, sortField: "portal" },
  { key: "uploaded_at", label: "Date", sortable: true, sortField: "date" },
  { key: "member_name", label: "Member", sortable: true, sortField: "member_name", hide: "xl", editable: true, editType: "select" },
  { key: "fund_name", label: "Fund", sortable: true, sortField: "fund_name", hide: "xl", editable: true, editType: "text" },
  { key: "doc_type", label: "Type", sortable: true, sortField: "doc_type", hide: "xl", editable: true, editType: "select", options: DOC_TYPE_OPTIONS },
  { key: "download", label: "", sortable: false, align: "right" },
];

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
      <div className="flex-1 space-y-1">
        <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="h-2 bg-gray-100 rounded w-1/2 animate-pulse" />
      </div>
      <div className="h-3 bg-gray-200 rounded w-16 animate-pulse" />
    </div>
  );
}

export default function DocumentTable({
  documents,
  sortBy,
  sortDir,
  onSort,
  loading,
  onEditField,
  onOverrideField,
  canonicalMembers = [],
  fallbackMemberNames = [],
  onOpenDoc,
  selectedIds,
  onSelectionChange,
  onUndone,
  onMarkDone,
}) {
  // Member dropdown options: canonical names from member_aliases.yaml.
  // When the YAML library is empty (tenants without curated canonicals yet),
  // fall back to the union of member names already seen on docs (sourced from
  // /api/documents/stats.member_names) so the dropdown isn't a useless empty
  // list. "Other…" remains available regardless.
  const canonicalNames = (canonicalMembers || [])
    .map((m) => m.canonical_name)
    .filter(Boolean);
  const memberOptions = canonicalNames.length > 0
    ? canonicalNames
    : Array.from(new Set((fallbackMemberNames || []).filter(Boolean))).sort();
  const editOverride = onOverrideField || onEditField;
  const [emailDialog, setEmailDialog] = useState({ open: false, doc: null });
  const navigate = useNavigate();
  const { rowSize } = useDocSizes();
  const rootRef = useRef(null);
  const [focusedId, setFocusedId] = useState(null);
  const getListRect = useCallback(() => rootRef.current?.getBoundingClientRect() ?? null, []);
  // One batched preview-urls call for the whole page, so thumbs find their URL cached.
  const prefetchKey = (documents || []).map((d) => d.blob_name).join("|");
  useEffect(() => {
    if (!prefetchKey) return;
    const paths = prefetchKey.split("|");
    Promise.resolve(prefetchPreviewUrls(paths)).catch(() => {});
    Promise.resolve(prefetchThumbUrls(paths.filter((p) => isPdfDoc("", p)))).catch(() => {});
  }, [prefetchKey]);
  const padY = 4; // thumb height = row height - 8
  const cellPad = { paddingTop: padY, paddingBottom: padY };
  const fontSize = Math.round((12 + ((Math.min(130, Math.max(22, rowSize)) - 22) * 8) / 108) * 10) / 10;
  const thumbBox = { width: rowSize, height: Math.round(rowSize * 1.3) };

  const openDoc = (doc) => {
    if (!doc.blob_name) return;
    if (isPdfDoc(doc.original_filename, doc.blob_name)) {
      onOpenDoc?.(doc);
      navigate(viewerPath(doc.blob_name));
    } else {
      downloadDoc(doc.blob_name);
    }
  };

  // Normalize selectedIds: accept Set or Array, always use a Set internally
  const selSet = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);

  // All visible doc ids
  const visibleIds = documents ? documents.map((d) => d.blob_name).filter(Boolean) : [];
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selSet.has(id));
  const someSelected = visibleIds.some((id) => selSet.has(id));
  const headerChecked = allSelected ? true : someSelected ? "indeterminate" : false;

  const handleHeaderCheckbox = (e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    if (allSelected) {
      // Deselect all visible
      const next = new Set(selSet);
      visibleIds.forEach((id) => next.delete(id));
      onSelectionChange(next);
    } else {
      // Select all visible
      const next = new Set(selSet);
      visibleIds.forEach((id) => next.add(id));
      onSelectionChange(next);
    }
  };

  const handleRowCheckbox = (e, blobName) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    const next = new Set(selSet);
    if (next.has(blobName)) {
      next.delete(blobName);
    } else {
      next.add(blobName);
    }
    onSelectionChange(next);
  };

  if (loading) {
    return (
      <Card>
        <div className="divide-y divide-gray-100">
          {[...Array(8)].map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </Card>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Card>
        <div className="p-12 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
            <span className="text-gray-400 text-lg">D</span>
          </div>
          <p className="text-sm text-gray-600 font-medium">No documents found</p>
          <p className="text-xs text-gray-400 mt-1">
            Try adjusting your filters or documents will appear here after connections run.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div ref={rootRef} className="overflow-x-clip">
      <table className="w-full">
        <thead className="sticky z-10 bg-white" style={{ top: "calc(var(--app-header-h, 0px) + var(--docs-filters-h, 0px))" }}>
          <tr className="border-b border-gray-200">
            {/* Checkbox header */}
            <th
              className="px-3 py-2.5 text-left w-8"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={headerChecked}
                onCheckedChange={() => {}}
                onClick={handleHeaderCheckbox}
                aria-label="Select all documents"
                className="border-gray-300"
              />
            </th>
            <th aria-hidden="true" />
            {columns.map((col) => (
              <th
                key={col.key}
                className={[
                  "text-[10px] font-medium text-gray-400 uppercase tracking-wider px-4 py-2.5",
                  col.align === "right" ? "text-right" : "text-left",
                  col.hide ? `hidden ${col.hide}:table-cell` : "",
                  col.sortable ? "cursor-pointer hover:text-gray-600 select-none" : "",
                ].join(" ")}
                style={col.width ? { width: col.width } : undefined}
                onClick={col.sortable ? () => onSort(col.sortField) : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortBy === col.sortField && (
                    sortDir === "desc" ? (
                      <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUp className="w-3 h-3" />
                    )
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {documents.map((doc, i) => (
            <tr
              key={doc.blob_name || i}
              className={[
                "hover:bg-gray-50 transition cursor-pointer",
                selSet.has(doc.blob_name) ? "bg-red-50" : "",
              ].join(" ")}
              tabIndex={0}
              style={{ fontSize }}
              data-doc-row
              onClick={() => openDoc(doc)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target === e.currentTarget) openDoc(doc);
              }}
              onFocus={(e) => {
                if (e.target === e.currentTarget) setFocusedId(doc.blob_name);
              }}
              onBlur={(e) => {
                if (e.target === e.currentTarget) setFocusedId(null);
              }}
            >
              {/* Row checkbox */}
              <td className="px-3 py-2.5 w-8" style={cellPad} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selSet.has(doc.blob_name)}
                  onCheckedChange={() => {}}
                  onClick={(e) => handleRowCheckbox(e, doc.blob_name)}
                  aria-label={`Select ${doc.original_filename || doc.blob_name}`}
                  className="border-gray-300"
                />
              </td>

              {/* Thumbnail + hover preview */}
              <td className="px-2" style={cellPad}>
                <Suspense fallback={<div className="shrink-0" style={thumbBox} />}>
                  <DocThumb
                    blobPath={doc.blob_name}
                    filename={doc.original_filename || doc.blob_name}
                    size={rowSize}
                    forceOpen={focusedId === doc.blob_name}
                    anchorListRect={getListRect}
                    onOpen={() => openDoc(doc)}
                  />
                </Suspense>
              </td>

              {/* Document name */}
              <td className="px-4 py-2.5" style={{ ...cellPad, width: DOC_COL_WIDTH }}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {doc.is_new && (
                    <span
                      data-testid="doc-new-badge"
                      className="inline-block text-[0.83em] font-semibold px-1.5 py-0.5 rounded text-white leading-none shrink-0"
                      style={{ backgroundColor: BRAND.bg }}
                    >
                      New
                    </span>
                  )}
                  {doc.is_done && (
                    <span
                      data-testid="doc-done-badge"
                      title={doneNoteText(doc) || undefined}
                      aria-describedby={doc.done_note ? `done-note-${doc.blob_name}` : undefined}
                      className={[
                        "inline-block text-[0.83em] font-semibold px-1.5 py-0.5 rounded leading-none shrink-0 bg-green-100 text-green-700",
                        doc.done_note ? "cursor-help" : "",
                      ].join(" ").trim()}
                    >
                      Done
                      {doc.done_note && (
                        <span id={`done-note-${doc.blob_name}`} className="sr-only">
                          {doneNoteText(doc)}
                        </span>
                      )}
                    </span>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p
                        className={[
                          "text-[1em] truncate max-w-[200px] cursor-help",
                          doc.is_done ? "text-gray-400" : "text-gray-900",
                        ].join(" ")}
                      >
                        {doc.display_name || doc.original_filename || doc.document_name || doc.blob_name}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-[480px] break-all whitespace-normal"
                    >
                      {doc.display_name || doc.original_filename || doc.document_name || doc.blob_name}
                    </TooltipContent>
                  </Tooltip>
                </div>
                {doc.document_category && (
                  <p className="text-[0.83em] text-gray-400">{doc.document_category}</p>
                )}
              </td>

              {/* Portal */}
              <td className="px-4 py-2.5" style={cellPad}>
                <span
                  className="text-[0.83em] px-2 py-0.5 rounded capitalize"
                  style={{ backgroundColor: BRAND.soft, color: BRAND.text }}
                >
                  {doc.source === "email" ? "Email" : doc.portal || "--"}
                </span>
              </td>

              {/* Date (editable) — Card 0: scraper-derived document_date wins
                  over V11/CU notice_date so the displayed value matches the
                  scraper-native source we sort by. */}
              <td className="px-4 py-2.5 text-[0.83em] text-gray-900" style={cellPad}>
                <EditableCell
                  value={(() => {
                    const raw = doc.document_date || doc.uploaded_at || "";
                    if (!raw) return "";
                    try { return new Date(raw).toISOString().slice(0, 10); } catch { return raw.slice(0, 10); }
                  })()}
                  type="date"
                  formatDisplay={(v) => formatDate(v)}
                  onSave={(val) => onEditField?.(doc.blob_name, "notice_date", val)}
                />
              </td>

              {/* Member (editable dropdown — canonical members from YAML library) */}
              <td className="px-4 py-2.5 hidden xl:table-cell text-[0.83em] text-gray-900" style={cellPad}>
                <EditableCell
                  value={doc.member_name || ""}
                  type="select"
                  options={memberOptions}
                  allowOther
                  onSave={(val) => editOverride?.(doc.blob_name, "member_name", val)}
                />
              </td>

              {/* Fund (editable) */}
              <td className="px-4 py-2.5 hidden xl:table-cell text-[0.83em] text-gray-900" style={cellPad}>
                <EditableCell
                  value={doc.fund_name || ""}
                  type="text"
                  onSave={(val) => editOverride?.(doc.blob_name, "fund_name", val)}
                />
              </td>

              {/* Type (editable dropdown — canonical doc types) */}
              <td className="px-4 py-2.5 hidden xl:table-cell text-[0.83em] text-gray-900" style={cellPad}>
                <EditableCell
                  value={doc.doc_type || ""}
                  type="select"
                  options={DOC_TYPE_OPTIONS}
                  allowOther
                  onSave={(val) => editOverride?.(doc.blob_name, "doc_type", val)}
                />
              </td>

              {/* Done toggle + Download + Send by email */}
              <td className="px-4 py-2.5 text-right" style={cellPad}>
                <div className="inline-flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        data-testid="doc-done-toggle"
                        aria-label={doc.is_done ? "Mark as not done" : "Mark as done"}
                        onClick={(e) => {
                          e.stopPropagation();
                          doc.is_done ? onUndone?.(doc.blob_name) : onMarkDone?.(doc.blob_name);
                        }}
                      >
                        {doc.is_done ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {doc.is_done ? "Mark as not done" : "Mark as done"}
                    </TooltipContent>
                  </Tooltip>
                  {doc.download_url ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[10px] gap-1"
                      style={{ color: BRAND.text }}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadDoc(doc.blob_name);
                      }}
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </Button>
                  ) : (
                    <span className="text-[0.83em] text-gray-300">--</span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    style={{ color: BRAND.text }}
                    title="Send by email"
                    aria-label="Send by email"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEmailDialog({ open: true, doc });
                    }}
                  >
                    <Mail className="w-3 h-3" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SendEmailDialog
        open={emailDialog.open}
        onOpenChange={(open) => setEmailDialog((prev) => ({ ...prev, open }))}
        doc={emailDialog.doc}
      />
    </div>
    </TooltipProvider>
  );
}
